import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { observer } from 'mobx-react-lite'
import { gql } from 'graphql-request'
import { useStores } from '@/components/StoreProvider'
import { authorizedGraphQLRequest } from '@/api'
import Layout from '@/components/Layout/Layout'
import { useTranslation } from '@/hooks'
import { Building2, GraduationCap, UserCircle } from 'lucide-react'

interface ModuleOption {
  id: number
  name: string
}

const GET_USER_MODULES = gql`
  query getUserModules {
    getUserModules {
      id
      name
    }
  }
`

const UserPage: React.FC = observer(() => {
  const navigate = useNavigate()
  const { userStore } = useStores()
  const { user } = userStore
  const { t } = useTranslation()
  const [modules, setModules] = useState<ModuleOption[]>([])
  const [isLoadingModules, setIsLoadingModules] = useState(true)

  // Waits for the authenticated user so a logged-out visit does not fire a doomed request
  useEffect(() => {
    if (!user?.id) return

    const fetchModules = async () => {
      try {
        setIsLoadingModules(true)
        const data = await authorizedGraphQLRequest<{ getUserModules: ModuleOption[] }>(GET_USER_MODULES)
        setModules(data.getUserModules || [])
      } catch (err) {
        // The module list is a secondary detail — a failed read degrades to the empty state
        console.error('Failed to fetch user modules:', err)
      } finally {
        setIsLoadingModules(false)
      }
    }
    fetchModules()
  }, [user?.id])

  const handleSignOut = () => {
    userStore.logout()
    navigate('/auth')
  }

  if (!user) {
    return (
      <Layout>
        <div className="bg-gray-100 min-h-screen pt-20 pb-6">
          <div className="container mx-auto p-4 max-w-2xl">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="text-center">
                <h2 className="text-xl font-semibold text-gray-700">{t('user.notLoggedIn')}</h2>
                <p className="mt-2 text-gray-600">{t('user.pleaseSignIn')}</p>
                <button onClick={() => navigate('/auth')} className="mt-4 bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg transition duration-200">
                  {t('auth.signIn')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 pb-8">
        <div className="container mx-auto p-4 max-w-3xl">
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl border-0 p-8 hover:shadow-2xl transition-all duration-300">
            <div className="flex justify-between items-center mb-8 border-b border-gray-100 pb-5">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-primary to-primary/70 rounded-xl shadow-md">
                  <UserCircle className="h-6 w-6 text-white" />
                </div>
                <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">{t('user.profile')}</h2>
              </div>
            </div>

            <div className="space-y-8">
              <div className="flex items-center space-x-6 p-6 bg-gradient-to-r from-gray-50 to-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-300">
                <div className="bg-gradient-to-br from-primary to-primary/80 rounded-2xl h-20 w-20 md:h-24 md:w-24 flex items-center justify-center shadow-lg hover:scale-105 transition-transform duration-300">
                  <span className="text-white text-3xl md:text-4xl font-bold">{user.firstName?.charAt(0)?.toUpperCase() || '?'}</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">
                    {user.firstName} {user.lastName}
                  </h3>
                  <p className="text-gray-500 mt-1 font-medium">{user.email}</p>
                  {user.organization_name && (
                    <p className="text-gray-500 mt-1 font-medium flex items-center gap-1.5">
                      <Building2 className="h-4 w-4 text-primary" />
                      {user.organization_name}
                    </p>
                  )}
                </div>
              </div>

              <div className="pt-2">
                <h4 className="text-xl font-bold mb-5 text-gray-800">{t('user.accountInfo')}</h4>
                <div className="grid grid-col-1 md:grid-cols-2 gap-6">
                  <div className="p-5 bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border-l-4 border-blue-500">
                    <p className="text-sm text-blue-700 font-semibold mb-2">{t('user.userId')}</p>
                    <p className="font-bold text-lg text-gray-800">{user.id}</p>
                  </div>
                  <div className="p-5 bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border-l-4 border-purple-500">
                    <p className="text-sm text-purple-700 font-semibold mb-2">{t('auth.email')}</p>
                    <p className="font-bold text-lg text-gray-800 break-all">{user.email}</p>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <h4 className="text-xl font-bold mb-5 text-gray-800">{t('user.yourModules')}</h4>
                {isLoadingModules ? (
                  <div className="flex items-center gap-3 p-5 bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-sm">
                    <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-gray-500 font-medium">{t('user.modulesLoading')}</p>
                  </div>
                ) : modules.length > 0 ? (
                  <div className="space-y-3">
                    {modules.map((module) => (
                      <div key={module.id} className="flex items-center gap-3 p-5 bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-xl shadow-sm border-l-4 border-emerald-500">
                        <GraduationCap className="h-5 w-5 text-emerald-600 shrink-0" />
                        <p className="font-bold text-lg text-gray-800">{module.name}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-5 bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-sm">
                    <p className="text-gray-500 font-medium">{t('user.noModules')}</p>
                  </div>
                )}
              </div>

              <div className="pt-2">
                <h4 className="text-xl font-bold mb-5 text-gray-800">{t('user.accountActions')}</h4>
                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/settings')}
                    className="w-full bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 text-gray-700 font-semibold py-4 px-6 rounded-xl transition-all duration-300 text-left shadow-md hover:shadow-lg hover:scale-[1.02] border-0"
                  >
                    {t('user.appSettings')}
                  </button>
                  <button
                    onClick={handleSignOut}
                    className="w-full bg-gradient-to-r from-red-50 to-red-100 hover:from-red-100 hover:to-red-200 text-red-600 font-semibold py-4 px-6 rounded-xl transition-all duration-300 text-left shadow-md hover:shadow-lg hover:scale-[1.02] border-0"
                  >
                    {t('auth.signOut')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
})

export default UserPage
